import { useState } from "react";
import { Link } from "react-router-dom";
import { InitialsAvatar } from "../../components/InitialsAvatar";
import { api, ApiError } from "../../lib/api";
import { useResource, formatDate, titleCase } from "../../lib/hooks";
import { useAuth } from "../../context/AuthContext";
import { FilterBar, matches } from "../../components/FilterBar";
import { EntityForm, Field } from "../../components/EntityForm";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { PrintDoc } from "../../components/PrintDoc";
import { Modal } from "../../components/Modal";
import { uploadFile, fileViewUrl, fileDownloadUrl } from "../../lib/files";
import type { Employee, Timesheet, TimesheetLine } from "../../lib/types";
import { BillableShiftsPreview } from "./Invoices";

const money = (value: string | number): string =>
    Number(value).toLocaleString("en-AU", { style: "currency", currency: "AUD" });

type Dialog =
    | { kind: "generate" }
    | { kind: "view"; timesheet: Timesheet }
    | { kind: "delete"; timesheet: Timesheet }
    | { kind: "bankGuard"; timesheet: Timesheet }
    | { kind: "importPayslips" }
    | null;

/**
 * Timesheets: delivered shifts priced at the employee's rate, approved, then
 * paid. The same one-shift-one-payment guard as invoicing, from the same
 * UNIQUE(shift_id) constraint. Staff see their own approved and paid
 * timesheets through this page — RLS scopes the list, the buttons are gated.
 *
 * `mineOnly` is the "My pay" mode: a manager or admin is a worker too, and
 * on that route they get only their own pay, with the management buttons off —
 * paying yourself is a job for the Timesheets module, in full view.
 */
export const Timesheets = ({ mineOnly = false }: { mineOnly?: boolean }) => {
    const { user, mayQuality } = useAuth();
    // Engine adoption (9 Aug 2026): matrix keys instead of role names, so a
    // delegated approver sees the buttons their grant unlocks server-side.
    const canManage = !mineOnly
        && ["timesheets.generate", "timesheets.approve", "timesheets.settle"]
            .some((key) => mayQuality(key));

    const timesheets = useResource<Timesheet[]>("/timesheets");
    const employees = useResource<Employee[]>(canManage ? "/employees" : null);
    const importedPayslips = useResource<ImportedPayslip[]>("/imported-payslips");

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [dialog, setDialog] = useState<Dialog>(null);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    const refresh = () => { timesheets.reload(); setDialog(null); };

    const transition = async (timesheet: Timesheet, action: string, body: object = {}) => {
        setBusyId(timesheet.id);
        setActionError(null);
        try {
            await api.post(`/timesheets/${timesheet.id}/${action}`, body);
            timesheets.reload();
        } catch (err) {
            // The 48-hour bank-change guard answers 409 with a confirmation
            // request rather than a refusal -- surface it as its own dialog.
            const guard = err instanceof ApiError
                && (err.body as { requiresConfirmation?: boolean } | null)?.requiresConfirmation;
            if (action === "mark-paid" && guard) {
                setDialog({ kind: "bankGuard", timesheet });
            } else {
                setActionError(err instanceof Error ? err.message : "Action failed");
            }
        } finally {
            setBusyId(null);
        }
    };

    const generateFields: Field[] = [
        { name: "employeeId", label: "Employee", kind: "select", required: true,
          options: (employees.data ?? []).map((e) => ({ value: e.id, label: e.full_name })) },
        { name: "periodStart", label: "Period from", kind: "date", required: true, half: true },
        { name: "periodEnd", label: "Period to", kind: "date", required: true, half: true },
        { name: "notes", label: "Notes", kind: "textarea" },
    ];

    // In mineOnly mode RLS already scopes staff to their own rows, but a
    // manager's RLS shows everyone — the personal page narrows to their own.
    const isMine = (employeeId: string) => !mineOnly || employeeId === user?.employeeId;
    const all = (timesheets.data ?? []).filter((t) => isMine(t.employee_id));
    const slips = (importedPayslips.data ?? []).filter((s) => isMine(s.employee_id));
    const filtered = all.filter((timesheet) =>
        matches(search, timesheet.timesheet_number, timesheet.employee_name) &&
        (!filters.status || timesheet.status === filters.status)
    );

    if (timesheets.error) return <div className="error-banner">{timesheets.error}</div>;

    if (mineOnly && !user?.employeeId) {
        return (
            <div className="card" style={{ maxWidth: 640 }}>
                <div className="card-body">
                    <p style={{ color: "var(--muted)" }}>
                        Your account is not linked to an employee record, so there is no pay to
                        show here. If you also work shifts, an administrator can link your
                        account to your employee record from Settings.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div>
            {dialog?.kind === "generate" && (
                <EntityForm
                    title="Generate Timesheet" path="/timesheets/generate" fields={generateFields}
                    subtitle="Built from the employee's delivered shifts in the period, at their pay rate, excluding anything already on a timesheet."
                    preview={(values) => (
                        <BillableShiftsPreview
                            employeeId={String(values.employeeId ?? "")}
                            from={String(values.periodStart ?? "")}
                            to={String(values.periodEnd ?? "")} />
                    )}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "view" && (
                <TimesheetDoc timesheet={dialog.timesheet} onClose={() => setDialog(null)} />
            )}
            {dialog?.kind === "bankGuard" && (
                <ConfirmDialog
                    title={`Bank details recently changed — ${dialog.timesheet.employee_name}`}
                    tone="danger"
                    message="This worker's bank details were changed within the last 48 hours. That timing is the classic account-takeover pattern."
                    warning="Verify the change with the worker directly — by phone or in person, never by replying to an email — before paying. Confirming here records that the guard fired and you overrode it deliberately."
                    confirmLabel="Verified — pay now"
                    request={{ method: "post", path: `/timesheets/${dialog.timesheet.id}/mark-paid`, body: { confirmed: true } }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}
            {dialog?.kind === "delete" && (
                <ConfirmDialog
                    title={`Delete draft ${dialog.timesheet.timesheet_number}?`}
                    tone="danger"
                    message="The draft is removed and its shifts return to the unpaid pool."
                    warning="Approved timesheets cannot be deleted — they are the record behind a payment."
                    confirmLabel="Delete draft"
                    request={{ method: "delete", path: `/timesheets/${dialog.timesheet.id}` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}

            {dialog?.kind === "importPayslips" && (
                <BulkPayslipDialog
                    employees={employees.data ?? []}
                    onClose={() => setDialog(null)}
                    onDone={() => { setDialog(null); importedPayslips.reload(); }}
                />
            )}

            {actionError && <div className="error-banner">{actionError}</div>}

            <FilterBar
                search={search} onSearch={setSearch}
                searchPlaceholder="Search timesheets…"
                values={filters}
                onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                filters={[{
                    name: "status", allLabel: "All Status",
                    options: ["draft", "approved", "paid"].map((s) => ({ value: s, label: titleCase(s) })),
                }]}
                action={canManage && (
                    <div style={{ display: "flex", gap: "0.4rem" }}>
                        <button className="btn ghost" onClick={() => setDialog({ kind: "importPayslips" })}>Import payslips</button>
                        <button className="btn" onClick={() => setDialog({ kind: "generate" })}>+ Generate Timesheet</button>
                    </div>
                )}
            />

            {slips.length > 0 && (
                <div className="card" style={{ marginBottom: "1rem" }}>
                    <div className="card-head">
                        <h2>Imported payslips</h2>
                        <span className="chip mute">from external payroll software</span>
                    </div>
                    <div className="card-body flush"><div className="table-wrap">
                        <table>
                            <thead><tr><th>Employee</th><th>Period</th><th>File</th><th>Source</th>
                                <th style={{ textAlign: "right" }}>Actions</th></tr></thead>
                            <tbody>
                                {slips.map((slip) => (
                                    <tr key={slip.id}>
                                        <td>
                                            <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", fontWeight: 600 }}>
                                                <InitialsAvatar name={slip.employee_name} size={24} />
                                                {slip.employee_name ?? "—"}
                                            </div>
                                        </td>
                                        <td>{slip.period_label}</td>
                                        <td><a href={fileViewUrl(slip.file_id)} target="_blank" rel="noreferrer">{slip.file_name}</a></td>
                                        <td>{slip.source ?? "—"}</td>
                                        <td><div className="row-actions">
                                            <a className="icon-btn" href={fileDownloadUrl(slip.file_id)}>Download</a>
                                            {canManage && (
                                                <button className="icon-btn danger" onClick={async () => {
                                                    await api.delete(`/imported-payslips/${slip.id}`);
                                                    importedPayslips.reload();
                                                }}>Remove</button>
                                            )}
                                        </div></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div></div>
                </div>
            )}

            <div className="card">
                <div className="card-body flush">
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Timesheet</th><th>Employee</th><th>Period</th>
                                    <th>Hours</th><th>Amount</th><th>Status</th>
                                    <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((timesheet) => (
                                    <tr key={timesheet.id}>
                                        <td className="mono" style={{ fontWeight: 600 }}>{timesheet.timesheet_number}</td>
                                        <td>
                                            <Link to={`/employees?id=${timesheet.employee_id}`}
                                                style={{ display: "flex", alignItems: "center", gap: "0.6rem", textDecoration: "none", color: "inherit", fontWeight: 600 }}>
                                                <InitialsAvatar name={timesheet.employee_name} size={26} />
                                                {timesheet.employee_name}
                                            </Link>
                                        </td>
                                        <td className="mono">
                                            {formatDate(timesheet.period_start)} – {formatDate(timesheet.period_end)}
                                        </td>
                                        <td className="mono">{Number(timesheet.total_hours).toFixed(2)}</td>
                                        <td className="mono" style={{ fontWeight: 600 }}>{money(timesheet.total_amount)}</td>
                                        <td>
                                            <span className={`chip ${timesheet.status === "paid" ? "pass" : timesheet.status === "approved" ? "info" : "warn"}`}>
                                                {timesheet.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="row-actions">
                                                <button className="icon-btn" onClick={() => setDialog({ kind: "view", timesheet })}>
                                                    View / PDF
                                                </button>
                                                {canManage && timesheet.status === "draft" && (
                                                    <>
                                                        <button className="icon-btn" disabled={busyId === timesheet.id}
                                                            onClick={() => void transition(timesheet, "approve")}>Approve</button>
                                                        <button className="icon-btn danger"
                                                            onClick={() => setDialog({ kind: "delete", timesheet })}>Delete</button>
                                                    </>
                                                )}
                                                {canManage && timesheet.status === "approved" && (
                                                    <button className="icon-btn" disabled={busyId === timesheet.id}
                                                        onClick={() => void transition(timesheet, "mark-paid")}>Mark paid</button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={7} className="empty">
                                        {all.length === 0 ? "No timesheets yet." : "No timesheets match."}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

const TimesheetDoc = ({ timesheet, onClose }: { timesheet: Timesheet; onClose: () => void }) => {
    const detail = useResource<Timesheet & { lines: TimesheetLine[] }>(`/timesheets/${timesheet.id}`);
    const data = detail.data;

    return (
        <PrintDoc kind={timesheet.basis === "abn" ? "TIMESHEET / PAYMENT ADVICE" : "TIMESHEET / PAYSLIP"}
            number={timesheet.timesheet_number} onClose={onClose}>
            {!data ? <p>Loading…</p> : (
                <>
                    <div className="doc-meta">
                        <div>
                            <div className="doc-label">Employee</div>
                            <div style={{ fontWeight: 700 }}>{data.employee_name}</div>
                        </div>
                        <div>
                            <div className="doc-label">Period</div>
                            <div>{formatDate(data.period_start)} – {formatDate(data.period_end)}</div>
                            <div>Status: {titleCase(data.status)}
                                {data.approved_at && ` · approved ${formatDate(data.approved_at)}`}
                                {data.paid_at && ` · paid ${formatDate(data.paid_at)}`}
                            </div>
                        </div>
                    </div>

                    <table className="doc-table">
                        <thead>
                            <tr><th>Date</th><th>Shift</th><th className="num">Hours</th><th className="num">Rate</th><th className="num">Amount</th></tr>
                        </thead>
                        <tbody>
                            {data.lines.map((line) => (
                                <tr key={line.id}>
                                    <td>{formatDate(line.work_date)}</td>
                                    <td>{line.description}</td>
                                    <td className="num">{Number(line.hours).toFixed(2)}</td>
                                    <td className="num">{money(line.hourly_rate)}</td>
                                    <td className="num">{money(line.amount)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="doc-total">
                                <td colSpan={2} className="num">Totals</td>
                                <td className="num">{Number(data.total_hours).toFixed(2)}</td>
                                <td />
                                <td className="num">{money(data.total_amount)}</td>
                            </tr>
                        </tfoot>
                    </table>

                    <div className="doc-meta" style={{ marginTop: "1.2rem" }}>
                        <div style={{ flex: 1 }}>
                            <div className="doc-label">Payment summary</div>
                            <table className="doc-table" style={{ maxWidth: 360 }}>
                                <tbody>
                                    <tr><td>Gross pay</td><td className="num">{money(data.total_amount)}</td></tr>
                                    {data.basis === "tfn" ? (
                                        <>
                                            <tr><td>PAYG withheld (estimate*)</td><td className="num">-{money(data.tax_withheld)}</td></tr>
                                            <tr className="doc-total"><td>Net pay</td><td className="num">{money(data.net_pay)}</td></tr>
                                            <tr><td>Superannuation (12% SG, paid to fund)</td><td className="num">{money(data.super_amount)}</td></tr>
                                        </>
                                    ) : (
                                        <tr className="doc-total"><td>Payable gross (ABN contractor)</td><td className="num">{money(data.net_pay)}</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <p className="doc-notes">
                        {data.basis === "tfn"
                            ? "*PAYG withheld here is a flat estimate agreed with the worker. Actual withholding must follow the ATO tax tables, and the authoritative payslip and income statement come from STP-enabled payroll software. Super is paid to the worker's fund on top of gross — from 1 July 2026, each payday."
                            : "Paid gross to an ABN contractor who remits their own tax. Note: the ATO treats contractors engaged mainly for their labour as eligible for superannuation guarantee — confirm this contractor's arrangement."}
                    </p>
                    {data.notes && <p className="doc-notes">{data.notes}</p>}
                </>
            )}
        </PrintDoc>
    );
};


interface ImportedPayslip {
    id: string;
    employee_id: string;
    file_id: string;
    file_name: string;
    period_label: string;
    source: string | null;
    employee_name: string | null;
    created_at: string;
}

interface PendingSlip {
    file: File;
    employeeId: string;
    periodLabel: string;
}

/**
 * Bulk payslip import: pick many files at once; each row is matched to a
 * worker (auto-guessed from the filename where possible) and labelled with
 * its pay period. Until Xero/QuickBooks connect directly, this is the
 * one-click-adjacent path: export from payroll, drop the lot here.
 */
const BulkPayslipDialog = ({ employees, onClose, onDone }: {
    employees: Employee[];
    onClose: () => void;
    onDone: () => void;
}) => {
    const [rows, setRows] = useState<PendingSlip[]>([]);
    const [source, setSource] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const guessEmployee = (fileName: string): string => {
        const lower = fileName.toLowerCase();
        const hit = employees.find((e) =>
            e.full_name.toLowerCase().split(/\s+/).every((part) => lower.includes(part)) ||
            lower.includes(e.last_name.toLowerCase()));
        return hit?.id ?? "";
    };

    const pickFiles = (list: FileList | null) => {
        if (!list) return;
        setRows((r) => [...r, ...[...list].map((file) => ({
            file,
            employeeId: guessEmployee(file.name),
            periodLabel: "",
        }))]);
    };

    const saveAll = async () => {
        setBusy(true);
        setError(null);
        try {
            for (const row of rows) {
                const stored = await uploadFile(row.file, "general");
                await api.post("/imported-payslips", {
                    employeeId: row.employeeId,
                    fileId: stored.id,
                    periodLabel: row.periodLabel.trim(),
                    source: source.trim() || undefined,
                });
            }
            onDone();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Import failed part-way — already-saved payslips are kept.");
            setBusy(false);
        }
    };

    const ready = rows.length > 0 && rows.every((r) => r.employeeId && r.periodLabel.trim().length >= 2);

    return (
        <Modal title="Import payslips" width="wide"
            subtitle="From your payroll software (Xero, QuickBooks…). Each file is matched to a worker and appears in their My pay."
            onClose={onClose}
            footer={<>
                <button className="btn" disabled={busy || !ready} onClick={() => void saveAll()}>
                    {busy ? "Importing…" : `Import ${rows.length} payslip${rows.length === 1 ? "" : "s"}`}
                </button>
                <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
            </>}>
            {error && <div className="error-banner">{error}</div>}
            <div className="form-grid">
                <div className="field half">
                    <label htmlFor="slip-files">Payslip files (PDF)</label>
                    <input id="slip-files" type="file" multiple accept=".pdf"
                        onChange={(e) => pickFiles(e.target.files)} />
                </div>
                <div className="field half">
                    <label htmlFor="slip-source">Source (optional)</label>
                    <input id="slip-source" value={source} onChange={(e) => setSource(e.target.value)}
                        placeholder="e.g. Xero" />
                </div>
            </div>
            {rows.length > 0 && (
                <div className="table-wrap" style={{ marginTop: "0.6rem" }}>
                    <table>
                        <thead><tr><th>File</th><th>Worker</th><th>Pay period label</th><th /></tr></thead>
                        <tbody>
                            {rows.map((row, index) => (
                                <tr key={index}>
                                    <td style={{ fontSize: "0.8rem" }}>{row.file.name}</td>
                                    <td>
                                        <select value={row.employeeId}
                                            aria-label={`Worker for ${row.file.name}`}
                                            onChange={(e) => setRows((r) => r.map((x, i) =>
                                                i === index ? { ...x, employeeId: e.target.value } : x))}>
                                            <option value="">— choose —</option>
                                            {employees.map((emp) => (
                                                <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td>
                                        <input value={row.periodLabel} placeholder="e.g. FN ending 19 Jul 2026"
                                            aria-label={`Period for ${row.file.name}`}
                                            onChange={(e) => setRows((r) => r.map((x, i) =>
                                                i === index ? { ...x, periodLabel: e.target.value } : x))} />
                                    </td>
                                    <td>
                                        <button className="icon-btn danger"
                                            onClick={() => setRows((r) => r.filter((_, i) => i !== index))}>
                                            Remove
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Modal>
    );
};
